import type { ReactNode } from "react"

import type { LucideIcon } from "@/components/icons"

import { Database as DbIcon, HardDrive, Share2 } from "@/components/icons"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent } from "@/components/ui/card"
import { useCountUp } from "@/hooks/use-count-up"

import { CollapsibleDashboardCard } from "./collapsible-dashboard-card"
import { useCollapsibleSection } from "../hooks/use-collapsible-section"

// Splits a pre-formatted KPI string (e.g. "€1,234", "82%", "1,234", "—") into
// the numeric portion to count-up animate and the surrounding chrome
// (currency prefix, unit suffix), which stays fixed during the animation.
// Returns null for non-numeric values (e.g. the "—" no-data placeholder),
// which are rendered as-is with no animation.
function parseNumericValue(
  value: string
): { numeric: number; prefix: string; suffix: string; grouped: boolean } | null {
  const match = /-?[\d,]+(?:\.\d+)?/.exec(value)
  if (!match) return null
  const numeric = Number(match[0].replace(/,/g, ""))
  if (!Number.isFinite(numeric)) return null
  return {
    numeric,
    prefix: value.slice(0, match.index),
    suffix: value.slice(match.index + match[0].length),
    grouped: match[0].includes(","),
  }
}

export type KPIProps = {
  icon: LucideIcon
  label: string
  sub?: string
  value: string
  isLoading?: boolean
}

type DashboardSectionCardProps = {
  actions?: ReactNode
  children: ReactNode
  description?: string
  icon: LucideIcon
  id: string
  title: string
}

// Mounts only once real data is available, so useCountUp's "animate on true
// initial mount only" guard counts up from 0 the first time the stat appears,
// per DESIGN.md §6.
export function TotalSessionsCount({ value }: { value: number }) {
  const animatedValue = useCountUp({ end: value, duration: 800 })
  return <>{animatedValue.toLocaleString()}</>
}

export function KPI({ icon: Icon, label, sub, value, isLoading }: KPIProps) {
  const parsed = parseNumericValue(value)
  const isAnimatable = parsed !== null
  const decimals = isAnimatable && !Number.isInteger(parsed.numeric) ? 2 : 0
  // `enabled` keeps the animate-once latch unarmed while the value is a
  // placeholder ("—"/loading), so the count-up plays when real data arrives.
  const animatedCount = useCountUp({
    end: isAnimatable ? parsed.numeric : 0,
    duration: 800,
    decimals,
    enabled: isAnimatable && !isLoading,
  })
  // During the animation, interpolate only the digit run and splice it back
  // between the original string's prefix/suffix (currency, %, units), using
  // the same grouping style as the source value; once settled, render the
  // literal `value` string so the chrome is preserved exactly.
  const settled = !isAnimatable || animatedCount === parsed.numeric
  const displayValue = settled
    ? value
    : `${parsed.prefix}${new Intl.NumberFormat("en-US", {
        useGrouping: parsed.grouped,
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      }).format(animatedCount)}${parsed.suffix}`

  return (
    <Card className="rounded-lg shadow-sm">
      <CardContent
        aria-label={`${label}: ${value}`}
        className="flex min-h-28 items-center gap-3 p-4 sm:min-h-0 sm:gap-4 sm:p-5"
        role="status"
      >
        <div className="bg-muted rounded-xl p-3">
          <Icon className="size-6" />
        </div>
        <div className="flex-1">
          <div className="text-2xl leading-tight font-semibold tabular-nums">
            {isLoading ? (
              <div className="flex items-center gap-2">
                <span className="opacity-50">{value}</span>
                <div className="size-4 animate-spin rounded-full border-2 border-current border-t-transparent opacity-50" />
              </div>
            ) : (
              displayValue
            )}
          </div>
          <div className="text-muted-foreground text-sm">{label}</div>
          {sub ? <div className="text-muted-foreground/80 mt-1 text-xs">{sub}</div> : null}
        </div>
      </CardContent>
    </Card>
  )
}

export function DashboardSectionCard({
  actions,
  children,
  description,
  icon: Icon,
  id,
  title,
}: DashboardSectionCardProps) {
  const section = useCollapsibleSection(`admin.dashboard.section.${id}`, true)
  return (
    <CollapsibleDashboardCard
      headerActions={actions}
      icon={Icon}
      id={id}
      open={section.isOpen}
      title={title}
      onOpenChange={section.setIsOpen}
    >
      {description ? <p className="text-muted-foreground mb-4 text-sm">{description}</p> : null}
      <div className="space-y-6">{children}</div>
    </CollapsibleDashboardCard>
  )
}

export function DataSourceIcon({ type }: { type: string }) {
  if (type === "database") {
    return <DbIcon className="size-5" />
  }

  if (type === "fileserver") {
    return <HardDrive className="size-5" />
  }

  return <Share2 className="size-5" />
}

const statusVariantMap: Record<string, "success" | "disabled" | "error" | "warning"> = {
  Connected: "success",
  Disabled: "disabled",
  Error: "error",
  Syncing: "warning",
}

export function StatusBadge({ status }: { status: string }) {
  return <Badge variant={statusVariantMap[status] ?? "disabled"}>{status}</Badge>
}
